import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../api';
import './SongSearch.css';

const formatDuration = (duration) => {
  if (!duration) return '';
  if (duration.includes(':')) return duration;
  const secs = parseInt(duration, 10);
  if (isNaN(secs)) return duration;
  const mins = Math.floor(secs / 60);
  const remainingSecs = secs % 60;
  return `${mins}:${remainingSecs.toString().padStart(2, '0')}`;
};

function SongSearch({ onSongSelect }) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const searchTimeout = useRef(null);

  useEffect(() => {
    // Debounce search - wait 300ms after user stops typing
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    if (searchQuery.length >= 2) {
      searchTimeout.current = setTimeout(() => {
        performSearch(searchQuery);
      }, 300);
    } else {
      setSearchResults([]);
      setHasSearched(false);
    }

    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [searchQuery]);

  const performSearch = async (query) => {
    setLoading(true);
    setHasSearched(true);

    try {
      const response = await api.get(`/api/songs/search?q=${encodeURIComponent(query)}`);
      setSearchResults(response.data);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (song) => {
    onSongSelect(song);
  };

  const getResultsText = (count) => {
    if (count === 50) {
      return t('search.resultsCountPlus', { count });
    }
    return t('search.resultsCount', { count });
  };

  return (
    <div className="song-search">
      <div className="search-box">
        <input
          type="text"
          placeholder={t('search.placeholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input"
          autoFocus
        />
        {loading && <div className="search-loading">{t('search.searching')}</div>}
      </div>

      {searchQuery.length > 0 && searchQuery.length < 2 && (
        <div className="search-hint">
          {t('search.hint')}
        </div>
      )}

      {hasSearched && searchResults.length === 0 && !loading && (
        <div className="no-results">
          {t('search.noResults', { query: searchQuery })}
        </div>
      )}

      {searchResults.length > 0 && (
        <div className="search-results">
          <div className="results-count">
            {getResultsText(searchResults.length)}
          </div>
          <ul className="results-list">
            {searchResults.map((song) => (
              <li
                key={song.id}
                className="result-item"
                onClick={() => handleSelect(song)}
              >
                <div className="song-info">
                  <div className="song-title">
                    {song.title}
                    <span className={`source-badge ${song.source}`}>
                      {song.source === 'karafun' ? 'KaraFun' : 'iSing'}
                    </span>
                  </div>
                  <div className="song-artist">{song.artist}</div>
                </div>
                <div className="song-duration">{formatDuration(song.duration)}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default SongSearch;
